import _, { startCase } from "lodash";
import { ProjectImage } from "../../containers/create-proposal/props";

export const capitalizeFirstLetter = (text: string) => {
  if (!text) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const capitalizeAllFirstLettersOfWords = (text: string) => {
  if (!text) {
    return text;
  }
  return text.split(' ').map(capitalizeFirstLetter).join(' ');
};

export const lowercaseFirstLetter = (word: string) => {
  return word.charAt(0).toLowerCase() + word.slice(1);
}

export const convertPascalCaseToWords = (value?: string) => {
  if (!value) {
    return value;
  }
  return (
    value
      // Look for long acronyms and filter out the last letter
      .replace(/([A-Z]+)([A-Z][a-z])/g, ' $1 $2')
      // Look for lower-case letters followed by upper-case letters
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      // Look for lower-case letters followed by numbers
      .replace(/([a-zA-Z])(\d)/g, '$1 $2')
      .replace(/^./, (str) => {
        return str.toUpperCase();
      })
      // Remove any white space left around the word
      .trim()
  );
};

export const htmlDecode = (input: string) => {
  if (!input) {
    return input;
  }
  const e = document.createElement('div');
  e.innerHTML = input;
  return e.childNodes[0].nodeValue;
};

export const generateUrl = (url: any, fileType: string) => {
  const pattern = `\\b(?:${['msword', // doc
  'vnd.openxmlformats-officedocument.wordprocessingml.document'// docx
  ].join("|")})\\b`;
  const regex = new RegExp(pattern);
  const previewableFile = regex.test(fileType?.trim());

  if (previewableFile) {
    const urlString = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
    return urlString
  }
  return url
};

export const getFloorLabel = (floorNumber: number) => {
  if (floorNumber === 0) return 'Ground Floor';
  if (floorNumber === 1) return 'First Floor';
  if (floorNumber === 2) return 'Second Floor';
  if (floorNumber === 3) return 'Third Floor';
  // Generate ordinal suffixes for 4th floor and above
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const remainder = floorNumber % 10;
  const suffix = remainder < 4 && Math.floor(floorNumber / 10) !== 1 ? suffixes[remainder] : suffixes[0];
  return `${floorNumber}${suffix} Floor`;
};

export const getPaymentStatus = (status:string | undefined)=>{
  if(status === 'PaymentCancelled'){
    return "Payment Cancelled"
  }else{
    return "Payment Collected"
  }
}

export const convertToTitleCase = (str:string) => {
  // Use lodash's `startCase` function to handle different cases
  return startCase(str.replace(/-/g, ' '));
}

type NestedObject = Record<string, any>;
export const flattenToDotNotation =(
    obj: NestedObject, 
    parentKey: string = '', 
    result: Record<string, any> = {}
): Record<string, any> => {
    for (const [key, value] of Object.entries(obj)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Recursively flatten nested objects
            flattenToDotNotation(value, newKey, result);
        } else {
            // Directly assign non-object values
            result[newKey] = value;
        }
    }
    return result;
}

export function groupProjects(data: ProjectImage[]) {
  // Ensure data is an array
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Group by projectId safely
  const grouped = _.groupBy(data, item => item?.projectId || "undefined");

  // Transform grouped data into the desired structure
  return _.map(grouped, (items, projectId) => {
    // Check if items array has valid data
    if (!items || items.length === 0) return null;

    const firstItem = items[0];
    const projectName = firstItem?.projectName || "Unknown Project";
    const imagePath = firstItem?.imagePath || "";

    const resImages = items
      .filter(item => item?.projectImageName && item?.projectImageID) // Only include valid items
      .map(item => ({
        projectImageName: item?.projectImageName || "",
        projectImageID: +item?.projectImageID ,
        projectID: +item?.projectID,
        projectImageTitle: item?.projectImageTitle || "",
        projectImageAltTag: item?.projectImageAltTag || "",
        imageCatID: +item?.imageCatID,
        projectImage: item?.projectImage || "",
        imageCatType: item?.imageCatType || ""
      }));

    // Only return valid projects
    if (!projectName || resImages.length === 0) return null;

    return {
      _id: projectId === "undefined" ? "" : projectId,
      projectName,
      resImages,
      imagePath
    };
  }).filter(Boolean); // Remove any null or undefined projects
}

export function transformProjectsData(projects:any) {
  if (!Array.isArray(projects)) return []; // Ensure input is an array

  return _.flatMap(projects, project => {
      if (!project || !project.resImages || !Array.isArray(project.resImages)) {
          return []; // Skip if `resImages` is not valid
      }

      return project.resImages.map((image:any) => ({
          projectId: project._id || "",
          projectName: project.projectName || "",
          imagePath: project.imagePath || "",
          projectImageName: image.projectImageName || "",
          projectImage: image.projectImage || "",
          projectImageID: image.projectImageID || "",
          projectID: image.projectID || "",
          projectImageTitle: image.projectImageTitle || "",
          projectImageAltTag: image.projectImageAltTag || "",
          imageCatID: image.imageCatID || "",
          imageCatType: image.imageCatType || ""
      }));
  });
}
